import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  InputAdornment,
  Paper,
  IconButton,
  TablePagination,
  Tooltip,
  Switch,
  Grid,
  Fab,
} from "@mui/material";
import { Edit, Delete, PictureAsPdf, Search, Add } from "@mui/icons-material";
import { Autocomplete } from "@mui/material";
import {
  obtenerListaProductos,
  actualizarProducto,
  eliminarProducto,
} from "../../../api/products.service";
import { notification, Modal as ModalAntd } from "antd";
import Loader from "../../ComponentesHome/Loader";

const ProductList = ({ onEdit, refresh }) => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedAlimentacion, setSelectedAlimentacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const { confirm } = ModalAntd;
  const [reload, setReload] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, [refresh, reload]);

  useEffect(() => {
    filterProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchTerm,
    products,
    selectedCategory,
    selectedBrand,
    selectedAlimentacion,
  ]);

  const fetchProducts = async () => {
    try {
      const response = await obtenerListaProductos();
      if (response.data.length > 0) {
        setProducts(response.data);
        setFilteredProducts(response.data);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = products;

    if (searchTerm) {
      const lowerCaseSearch = searchTerm.toLowerCase();
      filtered = filtered.filter((product) =>
        Object.values(product).join(" ").toLowerCase().includes(lowerCaseSearch)
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter(
        (product) => product.categoria?.titulo === selectedCategory
      );
    }

    if (selectedBrand) {
      filtered = filtered.filter((product) => product.marca === selectedBrand);
    }

    if (selectedAlimentacion) {
      filtered = filtered.filter(
        (product) => product.alimentacion === selectedAlimentacion
      );
    }

    setFilteredProducts(filtered);
  };

  const handleAutocompleteChange = (setter) => (event, value) => {
    setter(value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const toggleActiveStatus = async (id, currentStatus) => {
    try {
      await actualizarProducto(id, { active: !currentStatus });
      setReload(!reload);
    } catch (error) {
      console.error("Error updating product status:", error);
    }
  };

  const deleteProduct = async (id) => {
    confirm({
      title: "Eliminar producto seleccionado",
      content: "¿Está seguro de que desea eliminar el producto?",
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      async onOk() {
        try {
          await eliminarProducto(id);
          setReload(!reload);
          notification.success({
            message: "Producto eliminado",
            description: "El producto fue eliminado exitosamente.",
          });
        } catch (error) {
          console.error("Error al eliminar producto", error);
          notification.error({
            message: "Error",
            description:
              "No se pudo eliminar el producto, inténtelo nuevamente.",
          });
        }
      },
    });
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleEdit = (id) => {
    onEdit(id);
  };

  const handleCreate = () => {
    onEdit();
  };

  return (
    <Box p={3}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom={2}
        flexDirection={{ xs: "column", sm: "row" }}
      >
        <Typography variant="h4">Gestión de Productos</Typography>
        <TextField
          size="small"
          placeholder="Buscar productos..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: { xs: "100%", sm: "250px" } }}
        />
      </Box>

      {/* Filters */}
      <Grid container spacing={2} marginBottom={2}>
        <Grid item xs={12} sm={2}>
          <Autocomplete
            options={[
              ...new Set(products.map((p) => p.categoria?.titulo)),
            ].filter(Boolean)}
            getOptionLabel={(option) => option}
            value={selectedCategory}
            onChange={handleAutocompleteChange(setSelectedCategory)}
            renderInput={(params) => (
              <TextField {...params} label="Categoría" size="small" fullWidth />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <Autocomplete
            options={[...new Set(products.map((p) => p.marca))].filter(Boolean)}
            getOptionLabel={(option) => option}
            value={selectedBrand}
            onChange={handleAutocompleteChange(setSelectedBrand)}
            renderInput={(params) => (
              <TextField {...params} label="Marca" size="small" fullWidth />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <Autocomplete
            options={[...new Set(products.map((p) => p.alimentacion))].filter(
              Boolean
            )}
            getOptionLabel={(option) => option}
            value={selectedAlimentacion}
            onChange={handleAutocompleteChange(setSelectedAlimentacion)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Alimentación"
                size="small"
                fullWidth
              />
            )}
          />
        </Grid>
      </Grid>

      {/* Table */}
      {loading ? (
        <Loader />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Imagen</TableCell>
                <TableCell>Marca</TableCell>
                <TableCell>Modelo</TableCell>
                <TableCell>Capacidad</TableCell>
                <TableCell>Peso</TableCell>
                <TableCell>Altura</TableCell>
                <TableCell>Alimentación</TableCell>
                <TableCell>Categoría</TableCell>
                <TableCell>PDF</TableCell>
                <TableCell>Activo</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProducts
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((product) => (
                  <TableRow key={product._id}>
                    <TableCell>
                      <img
                        src={product.imagen}
                        alt={product.nombre}
                        style={{ width: "70px", height: "auto" }}
                      />
                    </TableCell>
                    <TableCell>{product.marca}</TableCell>
                    <TableCell>{product.modelo}</TableCell>
                    <TableCell>{product.capacidadCarga}</TableCell>
                    <TableCell>{product.peso}</TableCell>
                    <TableCell>{product.altura}</TableCell>
                    <TableCell>{product.alimentacion}</TableCell>
                    <TableCell>{product.categoria?.titulo}</TableCell>
                    <TableCell>
                      {product.pdfUrl !== "#" ? (
                        <Tooltip title="Ver PDF">
                          <a
                            href={product.pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <PictureAsPdf color="error" />
                          </a>
                        </Tooltip>
                      ) : (
                        "No disponible"
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        size="small"
                        checked={product.active}
                        onChange={() =>
                          toggleActiveStatus(product._id, product.active)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEdit(product._id)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => deleteProduct(product._id)}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredProducts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}
      <Fab
        color="primary"
        sx={{ position: "fixed", bottom: 16, right: 16 }}
        onClick={handleCreate}
      >
        <Add />
      </Fab>
    </Box>
  );
};

export default ProductList;
